/* eslint-disable @typescript-eslint/no-unnecessary-condition */
/* eslint-disable react-hooks/rules-of-hooks */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getKeyboardFocusableElements, createVisibilityChecker } from '../Animations/slide';
import ArrowButton from '../ArrowButtons/ArrowButton';
import { useInitialState } from '../../context/InitialStateContext';
import { matchI18n, useI18n } from '../../i18n';
import { useAppContext } from '../../context/Context';
import CarGroupCard from './Card';
import {
  ArrowWrapper,
  Container,
  ResultAndNavigationWrapper,
  SliderContainer,
} from './CarGroupStyles';
import type { MoxxCarlineGroup } from '../../../@types/MoxxTypes';
import { useTrackingManager } from '../../context/useTrackingManager';
import ExploreCompareButtons from '../ExploreCompareButtons/ExploreCompareButtons';
import { Text } from '@oneaudi/unified-web-components';

const CarGroup: React.FC = () => {
  const { i18nMessages } = useInitialState();
  const { carlineGroups } = useInitialState();

  if (carlineGroups === undefined) {
    return null;
  }
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { trackScrollOrSwipeEvent } = useTrackingManager();
  const {
    filteredCarlineGroups,
    selectedCarGroupId,
    setSelectedCarGroupId,
    logger,
    containerRef,
    selectedFilter,
  } = useAppContext();

  const [scrollState, setScrollState] = useState({
    scrolling: false,
    leftDisabled: true,
    rightDisabled: false,
    visibleCards: 1,
    hasScrolled: false,
  });
  const [carlineGroupsToDisplay, setCarlineGroupsToDisplay] = useState(
    filteredCarlineGroups || carlineGroups,
  );
  const minIndex = 0;

  const [maxIndex, setMaxIndex] = useState(carlineGroupsToDisplay.length - 1);
  const hasActiveFilter =
    filteredCarlineGroups && filteredCarlineGroups !== null && filteredCarlineGroups?.length > 0;
  const slideItemRefs = useMemo(
    () =>
      Array(carlineGroups.length)
        .fill(0)
        .map(() => React.createRef<HTMLLIElement>()),
    [],
  );

  const calculateVisibleCards = () => {
    if (!containerRef.current || slideItemRefs.length === 0) return;

    const containerWidth = containerRef.current.offsetWidth;
    let totalWidth = 0;
    let visibleCount = 0;
    const visibilityThreshold = 0.9; // card must be at least 90% visible

    for (let i = 0; i < slideItemRefs.length; i++) {
      const el = slideItemRefs[i]?.current;
      if (!el) continue;
      const slideWidth = el.offsetWidth;

      const newTotal = totalWidth + slideWidth;

      if (newTotal <= containerWidth) {
        // fully fits, count it
        visibleCount++;
        totalWidth = newTotal;
      } else {
        // check if at least 90% of the card is visible
        const visiblePart = containerWidth - totalWidth;
        const visibleRatio = visiblePart / slideWidth;

        if (visibleRatio >= visibilityThreshold) {
          visibleCount++;
        }
        break;
      }
    }

    setScrollState((prevState) => ({
      ...prevState,
      visibleCards: visibleCount,
      rightDisabled: prevState.visibleCards >= maxIndex,
    }));
  };

  const getCurrentLeftIndex = useCallback(() => {
    const container = containerRef.current;
    if (container) {
      return Math.min(
        ...slideItemRefs
          .map(({ current }, index) => (createVisibilityChecker(container, current) ? index : -1))
          .filter((value) => value !== -1),
      );
    }
    return 0;
  }, [containerRef, slideItemRefs]);

  const scrollToIndex = useCallback(
    (index: number) => {
      const slideItemRef = slideItemRefs[index];
      if (containerRef.current && slideItemRef?.current) {
        const { left: containerLeft } = containerRef.current.getBoundingClientRect();
        const { left } = slideItemRef.current.getBoundingClientRect();
        containerRef.current?.scrollBy({
          left: Math.floor(left - containerLeft),
          behavior: 'smooth',
        });
      }
    },
    [containerRef, slideItemRefs],
  );

  const scrollFullyIntoView = (index: number) => {
    if (containerRef.current) {
      scrollToIndex(index);
    }
  };

  const scrollTo = useCallback(
    (direction: 'left' | 'right') => {
      if (containerRef.current) {
        const currentIndex = getCurrentLeftIndex();
        // Reset selectedCarGroupId when scrolling
        if (setSelectedCarGroupId) {
          setSelectedCarGroupId(undefined);
        }
        let newIndex: number = 0;
        if (direction === 'left') {
          newIndex = Math.max(minIndex, currentIndex - scrollState.visibleCards);
        } else if (direction === 'right') {
          newIndex = Math.min(maxIndex, currentIndex + scrollState.visibleCards);
        }
        setScrollState((prevState) => ({
          ...prevState,
          leftDisabled: newIndex === 0,
          rightDisabled: newIndex + prevState.visibleCards >= maxIndex,
        }));

        if (newIndex !== undefined && newIndex !== currentIndex) {
          if (!scrollState.hasScrolled) {
            // to fire scroll tracking only once
            trackScrollOrSwipeEvent(selectedFilter, 'click');
            setScrollState((prevState) => ({
              ...prevState,
              hasScrolled: true,
            }));
          }
          scrollToIndex(newIndex);
        }
      }
    },
    [
      containerRef,
      getCurrentLeftIndex,
      scrollToIndex,
      scrollState.visibleCards,
      scrollState.hasScrolled,
      minIndex,
      maxIndex,
    ],
  );

  const amountCarlines = (amountModelle: number) => {
    const i18Key = amountModelle === 1 ? 'numberOfResult' : 'numberOfResults';
    return matchI18n(i18Key, i18nMessages, {
      value: amountModelle.toString(),
    });
  };

  const handleCardClick = (cargroupId: string) => {
    if (setSelectedCarGroupId) {
      const isSelected = cargroupId === selectedCarGroupId;
      setSelectedCarGroupId(isSelected ? undefined : cargroupId);
    } else {
      logger?.warn('setSelectedCarGroupId is undefined');
    }
  };

  // track mobile swipe event only once
  const handleTouchEnd = () => {
    const minSwipeDistance = 30; // in px
    const shouldTrackSwipe =
      containerRef.current &&
      containerRef.current.scrollLeft >= minSwipeDistance &&
      !scrollState.hasScrolled;

    if (shouldTrackSwipe) {
      trackScrollOrSwipeEvent(selectedFilter, 'swipe');
      setScrollState((prevState) => ({ ...prevState, hasScrolled: true }));
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;

      const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;

      setScrollState((prevState) => ({
        ...prevState,
        leftDisabled: scrollLeft <= 0,
        rightDisabled: scrollLeft + clientWidth >= scrollWidth,
        scrolling: false,
      }));
    };

    const containerElement = containerRef.current;
    if (containerElement) {
      containerElement.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (containerElement) {
        containerElement.removeEventListener('scroll', handleScroll);
      }
    };
  }, [containerRef, slideItemRefs, scrollState.visibleCards, maxIndex]);

  /**
   * a11y improvement - scroll to last card when focus is within
   * e.g. navigate back from preview button
   */
  useEffect(() => {
    const focusHandler = () => {
      if (!scrollState.rightDisabled) {
        scrollTo('right');
      }
    };

    const lastCard = slideItemRefs[slideItemRefs.length - 1]?.current;
    if (lastCard) {
      const focusableElements = getKeyboardFocusableElements(lastCard);

      focusableElements.forEach((element) => {
        element.addEventListener('focus', focusHandler);
      });
    }

    return () => {
      if (lastCard) {
        const focusableElements = getKeyboardFocusableElements(lastCard);

        focusableElements.forEach((element) => {
          element.removeEventListener('focus', focusHandler);
        });
      }
    };
  }, [scrollState.rightDisabled]);

  useEffect(() => {
    calculateVisibleCards(); // Initial calculation

    const resizeObserver = new ResizeObserver(() => {
      calculateVisibleCards(); // Recalculate on container resize
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        resizeObserver.unobserve(containerRef.current);
      }
    };
  }, [containerRef, slideItemRefs, maxIndex]);

  useEffect(() => {
    setCarlineGroupsToDisplay(filteredCarlineGroups || carlineGroups);
  }, [filteredCarlineGroups, carlineGroups, hasActiveFilter]);

  useEffect(() => {
    setMaxIndex(() => {
      const newMaxIndex = carlineGroupsToDisplay.length;
      return newMaxIndex;
    });
    // reset card position
    if (containerRef && containerRef.current) {
      containerRef.current.scrollLeft = 0;
    }
  }, [carlineGroupsToDisplay.length]);

  useEffect(() => {
    setScrollState((prevState) => {
      const leftIndex = getCurrentLeftIndex();

      return {
        ...prevState,
        leftDisabled: leftIndex === 0,
        rightDisabled: leftIndex + scrollState.visibleCards >= maxIndex,
        scrolling: false,
      };
    });
  }, [maxIndex]);

  const arrowLeftAria = useI18n('arrowLeft');
  const arrowRightAria = useI18n('arrowRight');
  const isLegacy = process.env.IS_LEGACY;
  const carlineCountResults =
    filteredCarlineGroups?.reduce((total, group) => {
      if (selectedCarGroupId && group.id !== selectedCarGroupId) {
        return total;
      }
      return total + (group.carlines?.length ?? 0);
    }, 0) ?? 0;
  return (
    <>
      <Container>
        {!isLegacy && (
          <ResultAndNavigationWrapper align="center" justify="space-between">
            <Text variant={{ type: 'copy', order: '3', style: 'normal' }}>
              {amountCarlines(carlineCountResults)}
            </Text>
            <ArrowWrapper>
              <ArrowButton
                data-testid="arrow-button-left"
                onClick={() => {
                  scrollTo('left');
                }}
                disabled={scrollState.leftDisabled || scrollState.scrolling}
                direction="left"
                variant="tertiary"
                arialabel={arrowLeftAria}
              />
              <ArrowButton
                data-testid="arrow-button-right"
                onClick={() => {
                  scrollTo('right');
                }}
                disabled={scrollState.rightDisabled || scrollState.scrolling}
                direction="right"
                variant="tertiary"
                arialabel={arrowRightAria}
              />
            </ArrowWrapper>
          </ResultAndNavigationWrapper>
        )}
        <SliderContainer
          ref={containerRef}
          onTouchEnd={handleTouchEnd}
          data-testid="slider-container"
        >
          {carlineGroupsToDisplay?.map((carlineGroup: MoxxCarlineGroup, index: number) => {
            return (
              <CarGroupCard
                key={`${carlineGroup.name}-${index}`}
                ref={slideItemRefs[index]}
                carlineGroup={carlineGroup}
                isSelected={carlineGroup.id === selectedCarGroupId}
                dataTestId={`slider-item-${index}`}
                onClick={() => {
                  handleCardClick(carlineGroup.id);
                  if (
                    containerRef &&
                    containerRef.current &&
                    slideItemRefs[index] &&
                    slideItemRefs[index].current
                  ) {
                    const isVisibleItem = createVisibilityChecker(
                      containerRef.current,
                      slideItemRefs[index].current,
                    );
                    if (!isVisibleItem) {
                      scrollFullyIntoView(index);
                    }
                  }
                }}
                amountCarlines={amountCarlines(carlineGroup.carlines.length)}
              />
            );
          })}
        </SliderContainer>
        {isLegacy && (
          <>
            <ArrowButton
              data-testid="arrow-button-left"
              onClick={() => {
                scrollTo('left');
              }}
              disabled={scrollState.leftDisabled || scrollState.scrolling}
              direction="left"
              variant="tertiary"
              arialabel={arrowLeftAria}
            />
            <ArrowButton
              data-testid="arrow-button-right"
              onClick={() => {
                scrollTo('right');
              }}
              disabled={scrollState.rightDisabled || scrollState.scrolling}
              direction="right"
              variant="tertiary"
              arialabel={arrowRightAria}
            />
          </>
        )}
      </Container>
      {typeof selectedCarGroupId === 'undefined' && <ExploreCompareButtons />}
    </>
  );
};

export default CarGroup;
